import { TrophyOutlined, GiftOutlined, LikeOutlined, EyeOutlined, CheckCircleOutlined, UploadOutlined, ConsoleSqlOutlined } from '@ant-design/icons';
import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { Modal, Input, Button, Steps, List, Avatar, Select, message as antMessage, Popover, Spin, Tooltip, Checkbox } from 'antd';
import axios from 'axios';
import type { StepsProps } from 'antd';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { ActionCardInsidewig } from '../Atoms/ActionCardInsidewig';

import Birthday from '../../assets/images/Celebrate_Logos/Birthday.svg';
import WorkAnniverary from '../../assets/images/Celebrate_Logos/Work_Anniversary.svg';
import Festival from '../../assets/images/Celebrate_Logos/Festival.svg';

import HappyNewYear from '../../assets/images/Celebrate_Logos/Festivals/HappyNewYear.png';
import HappyHalloween from '../../assets/images/Celebrate_Logos/Festivals/HappyHalloween.png';
import FriendshipDay from '../../assets/images/Celebrate_Logos/Festivals/FriendshipDay.png';
import HappyDiwali from '../../assets/images/Celebrate_Logos/Festivals/HappyDiwali.png';
import HappyThanksgiving from '../../assets/images/Celebrate_Logos/Festivals/HappyThanksGiving.png';
import MerryChristmas from '../../assets/images/Celebrate_Logos/Festivals/MerryChristmas.png';
import { useTranslation } from 'react-i18next';

const { Step } = Steps;
const { TextArea } = Input;
const { Option } = Select;

interface CelebrateProps {
  visible: boolean;
  onClose: () => void;
}

// Props for the eCards
interface ECard {
  id: string;
  imageBytes: string;
  title?: string;
  background?: string;
}

// Props for the recipient
interface Recipient {
  id: string;
  displayName: string;
  email: string;
}

const Celebrate: React.FC<CelebrateProps> = ({ visible, onClose }) => {
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [recipient, setRecipient] = useState<string | null>(null);
  const [recipientDetails, setRecipientDetails] = useState<Recipient | null>(null);
  const [nominatorName, setNominatorName] = useState('');
  const [nominatorEmail, setNominatorEmail] = useState('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [recipientSearchResults, setRecipientSearchResults] = useState<Recipient[]>([]);
  const [recipientSearchResultsSameTeam, setRecipientSearchResultsSameTeam] = useState<Recipient[]>([]);
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const [showAllCards, setShowAllCards] = useState(false);
  const [showCardsModal, setShowCardsModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedEcardData, setSelectedEcardData] = useState<ECard | null>(null);
  const [description, setDescription] = useState<string>('');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showFestivalModal, setShowFestivalModal] = useState(false);
  const [ecards, setEcards] = useState<ECard[]>([]);
  const [categorycards, setCategoryCards] = useState<{ id: string; icon: JSX.Element; title: string; background: string }[]>([]);
  const [festivalCategories, setFestivalCategories] = useState<{ celebrationCategoryId: string; name: string }[]>([]);
  const [selectedFestivalCategory, setSelectedFestivalCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isVisibleToSocialWall, setIsVisibleToSocialWall] = useState(false);
  const { instance, accounts } = useMsal();
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const { t } = useTranslation();

  const backgroundColors = [
    'linear-gradient(238.85deg, #F3E8FF 0%, #E879F9 100%)',
    'linear-gradient(238.85deg, #E0F2FE 0%, #60A5FA 100%)',
    'linear-gradient(238.85deg, #FEF3C7 0%, #FBBF24 100%)',
    'linear-gradient(238.85deg, #DCFCE7 0%, #4ADE80 100%)',
    'linear-gradient(238.85deg, #D1FAE5 0%, #34D399 100%)',
    'linear-gradient(238.85deg, #F3F4F6 0%, #9CA3AF 100%)',
  ];

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  useEffect(() => {
    fetchToken();
  }, []);
  // Handle file selection
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedFormats.includes(file.type)) {
        antMessage.error('Invalid file format. Please upload a jpeg, jpg, or png file.');
        return;
      }
      setUploadedImage(file);
      antMessage.success('Image selected successfully!');
    }
  };

  // 1) FETCH MAIN CELEBRATION CATEGORIES
  const fetchCelebrationCategories = async () => {
    const token = await fetchToken();
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CelebrationCategories/MainCategories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const categories = response.data.map((category: { celebrationCategoryId: string; name: string }, index: number) => {
        let icon;
        let background = backgroundColors[index % backgroundColors.length];
        switch (category.name) {
          case 'Happy Birthday':
            {
              icon = <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                <img src={Birthday} height={'100px'} width={'100px'} alt="Celebrate" className="w-8 h-8 object-contain" />
              </div>;
            }
            break;
          case 'Work Anniversary':
            {
              icon = <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                <img src={WorkAnniverary} height={'100px'} width={'100px'} alt="Celebrate" className="w-8 h-8 object-contain" />
              </div>;
            } break;
          case 'Festivals':
            {
              icon = <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                <img src={Festival} height={'100px'} width={'100px'} alt="Celebrate" className="w-8 h-8 object-contain" />
              </div>;
            }
            break;
          default:
            icon = <GiftOutlined style={{ fontSize: '64px' }} />;
        }

        return {
          id: category.celebrationCategoryId,
          icon,
          title: category.name,
          background,
        };
      });
      setCategoryCards(categories);
    } catch (error) {
      console.error('Error fetching celebration categories:', error);
    }
  };

  useEffect(() => {
    fetchCelebrationCategories();
  }, []);


  // 2) FETCH E-CARDS FOR "HAPPY BIRTHDAY" OR "WORK ANNIVERSARY"
  const fetchEcardsForBirthdayAndWorkAnniversaryCategory = async (category: string) => {
    try {
      const token = await fetchToken();
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CelebrationEcards/SelectedCategoriesEcards`, {
        params: { category },
        headers: { Authorization: `Bearer ${token}` }
      });

      // const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CelebrationEcards/SelectedCategoriesEcards`, {
      //   params: { category }

      // });
      const ecardsWithBase64 = response.data.map((ecard: { ecardId: string; image: string }) => ({
        id: ecard.ecardId,
        imageBytes: `data:image/jpeg;base64,${ecard.image}`,
      }));
      setEcards(ecardsWithBase64);
    } catch (error) {
      console.error('Error fetching eCards:', error);
    } finally {
      setLoading(false);
    }
  };

  // Whenever user picks "Happy Birthday" or "Work Anniversary", load eCards
  useEffect(() => {
    if (selectedCard && selectedCard !== 'Festivals') {
      fetchEcardsForBirthdayAndWorkAnniversaryCategory(selectedCard);
    }
  }, [selectedCard]);


  // 3) FETCH FESTIVAL SUB-CATEGORIES & FESTIVAL E-CARDS
  const fetchFestivalCategories = async () => {
    try {
      setLoading(true);
      const token = await fetchToken();

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CelebrationEcards/FestivalCategories`, { headers: { Authorization: `Bearer ${token}` } });
      setFestivalCategories(response.data);
    } catch (error) {
      console.error("Error fetching festival categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFestivalEcards = async (categoryName: string) => {
    try {
      setLoading(true);
      const token = await fetchToken();

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CelebrationEcards/FestivalEcards`, {
        params: { categoryName },
        headers: {
          Authorization: `Bearer ${token
            }`
        }
      });

      // const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/CelebrationEcards/FestivalEcards`, {
      //   params: { categoryName }
      // });
      const ecardsWithBase64 = response.data.map((ecard: { ecardId: string, image: string }) => ({
        id: ecard.ecardId,
        imageBytes: `data:image/jpeg;base64,${ecard.image}`,
      }));
      setEcards(ecardsWithBase64);
    } catch (error) {
      console.error("Error fetching festival eCards:", error);
    } finally {
      setLoading(false);
    }
  };

  // If user selects "Festivals" as category => fetch sub-categories
  useEffect(() => {
    if (selectedCard === 'Festivals') {
      fetchFestivalCategories();
    }
  }, [selectedCard]);

  // When user selects a festival from the dropdown
  const handleFestivalCategorySelect = (id: string, name: string) => {
    setSelectedFestivalCategory(name);
    fetchFestivalEcards(id);
    setHasUnsavedChanges(true);
  };

  // 4) AZURE AD & USER LOOKUP
  useEffect(() => {
    if (currentStep === 2 && selectedCard === 'Festivals') {
      // For "Festivals" => Step 2 is "Select Recipient"
      const fetchTeamMembersData = async () => {
        const employeeId = await fetchEmployeeId();
        if (employeeId) {
          fetchTeamMembers(employeeId);
        }
      };
      fetchTeamMembersData();
    } else if (currentStep === 1 && selectedCard !== 'Festivals') {
      // For non-Festivals => Step 1 is "Select Recipient"
      const fetchTeamMembersData = async () => {
        const employeeId = await fetchEmployeeId();
        if (employeeId) {
          fetchTeamMembers(employeeId);
        }
      };
      fetchTeamMembersData();
    }
  }, [currentStep, selectedCard]);

  const callMsGraph = async (token: any) => {
    const headers = new Headers();
    headers.append('Authorization', `Bearer ${token}`);
    const response = await fetch(`https://graph.microsoft.com/v1.0/me?`, {
      method: 'GET',
      headers,
    }).then((res) => res.json());
    return response;
  };

  const requestProfileData = useCallback(async () => {
    const token = await fetchToken();
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read', 'User.Read.All'],
        authority: `https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6`,
      });
      const data = await callMsGraph(response.accessToken);
      localStorage.setItem('UserMail', data?.mail?.toLowerCase());
      setNominatorName(data?.displayName || 'Default Nominator');
      setNominatorEmail(data?.mail?.toLowerCase() || '');
    } catch (error) {
      console.error('Silent token acquisition failed, acquiring token using redirect');
      instance.loginRedirect(loginRequest);
    }
  }, [instance]);

  useEffect(() => {
    requestProfileData();
  }, [requestProfileData]);

  const fetchEmployeeId = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL;
      const token = await fetchToken();

      const response = await axios.get(`${baseURL}/api/Users/CheckEmail`, {
        params: { email: nominatorEmail },
        headers: {
          Authorization: `Bearer ${token
            }`
        }
      });
      if (response.status === 200) {
        const employeeId = response.data.userId;
        return employeeId;
      }
    } catch (error) {
      console.error('Error fetching employee ID:', error);
      return null;
    }
  };

  // Search recipients as user types
  useEffect(() => {
    const fetchNomineeData = async () => {
      if (searchTerm.length < 3) {
        setRecipientSearchResults([]);
        return;
      }
      try {
        const token = await fetchToken();

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/search?search=${encodeURIComponent(searchTerm)}`, { headers: { Authorization: `Bearer ${token}` } });
        setRecipientSearchResults(response.data);
      } catch (error) {
        console.error('Error fetching recipients:', error);
        setRecipientSearchResults([]);
      }
    };

    fetchNomineeData();
  }, [searchTerm]);

  // Set recipient
  const handleRecipientSelect = async (value: string, option: any) => {
    try {
      const employeeId = await fetchEmployeeId();
      if (option.key === employeeId) {
        antMessage.error('You cannot select yourself as a recipient.');
        return;
      }

      // Find the recipient from both search results and team members
      const foundRecipient = [...recipientSearchResults, ...recipientSearchResultsSameTeam]
        .find(item => item.id === option.key);

      if (foundRecipient) {
        setRecipientId(foundRecipient.id);
        setRecipient(foundRecipient.displayName);
        setRecipientDetails(foundRecipient);
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.error('Error checking recipient selection:', error);

      // Allow selection to proceed even if validation fails
      const foundRecipient = [...recipientSearchResults, ...recipientSearchResultsSameTeam]
        .find(item => item.id === option.key);

      if (foundRecipient) {
        setRecipientId(foundRecipient.id);
        setRecipient(foundRecipient.displayName);
        setRecipientDetails(foundRecipient);
        setHasUnsavedChanges(true);
      }
    }
  };

  // Fetch team members
  const fetchTeamMembers = async (userId: string) => {
    try {
      const token = await fetchToken();

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/Users/SameManager`, {
        params: { userId },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setRecipientSearchResultsSameTeam(response.data);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const getColorFromName = (name: string) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
  };

  const handleCheckboxChange = (e: any) => {
    setIsVisibleToSocialWall(e.target.checked);
  };

  // 5) HANDLE STEPS & FORM ACTIONS
  const handleCardClick = (title: string) => {
    setHasUnsavedChanges(true);
    setSelectedCard(title);

    // Reset festival sub-category if user reselects categories
    if (title !== 'Festivals') {
      setSelectedFestivalCategory(null);
    }
    setCurrentStep(1);
  };

  const handleNextStep = () => {
    // Optional: Validate if user selected a festival sub-category in Step 1
    if (selectedCard === 'Festivals' && currentStep === 1 && !selectedFestivalCategory) {
      antMessage.error('Please select a festival before proceeding.');
      return;
    }

    setCurrentStep(currentStep + 1);
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const resetForm = () => {
    setSelectedCard(null);
    setCurrentStep(0);
    setRecipient(null);
    setRecipientDetails(null);
    setRecipientId(null);
    setSelectedImage(null);
    setSelectedImageId(null);
    setSelectedEcardData(null);
    setDescription('');
    setIsFullScreen(false);
    setHasUnsavedChanges(false);
    setEcards([]);
    setSelectedFestivalCategory(null);
    setUploadedImage(null);
  };

  const handleModalClose = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close the form? Your changes will be lost.',
        onOk: () => {
          resetForm();
          onClose();
        },
      });
    } else {
      resetForm();
      onClose();
    }
  };

  const handleSubmit = async () => {
    const nominatorId = await fetchEmployeeId();
    let celebrationCategoryId = null;

    if (selectedCard === 'Festivals' && selectedFestivalCategory) {
      celebrationCategoryId = festivalCategories.find(category => category.celebrationCategoryId === selectedFestivalCategory)?.celebrationCategoryId;
    } else {
      celebrationCategoryId = categorycards.find(card => card.title === selectedCard)?.id;
    }

    if (!celebrationCategoryId) {
      antMessage.error('Failed to find the celebration category ID.');
      return;
    }

    const formData = new FormData();
    formData.append('CelebrationCategoryId', celebrationCategoryId);
    formData.append('ecard', selectedImageId || '');
    formData.append('NominatorId', nominatorId || '');
    formData.append('NomineeId', recipientId || '');
    formData.append('Description', description);
    formData.append('IsSystemGenerated', 'false');
    formData.append('EventDate', new Date().toISOString());
    formData.append('Country', '');
    formData.append('Visibility', isVisibleToSocialWall.toString());

    // Convert uploadedImage to byte array and append it
    if (uploadedImage) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageBytes = e.target?.result;
        if (imageBytes) {
          formData.append('UploadedImage', new Blob([imageBytes], { type: uploadedImage.type }));
        }

        try {
          const token = await fetchToken();
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Celebrations`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          });

          if (response.status === 201) {
            const emailData = {
              subject: "Aptean Thanks - Celebrates",
              celebrationId: response.data.celebrationId
            };
            resetForm();
            onClose();
            antMessage.success(`You have Wished ${recipient} for ${selectedCard}!`);
            const response1 = await axios.post(`${process.env.REACT_APP_API_URL}/api/EmailNotification/SendEmailAcknowledgement`, emailData, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            console.error('Failed to submit celebration:', response.data);
          }
        } catch (error) {
          console.error('Error submitting celebration:', error);
        }
      };
      reader.readAsArrayBuffer(uploadedImage); // Read the file as an ArrayBuffer
    } else {
      try {
        const token = await fetchToken();
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/Celebrations`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.status === 201) {
          const emailData = {
            subject: "Aptean Thanks - Celebrates",
            celebrationId: response.data.celebrationId
          };
          resetForm();
          onClose();
          antMessage.success(`You have Wished ${recipient} for ${selectedCard}!`);
          const response1 = await axios.post(`${process.env.REACT_APP_API_URL}/api/EmailNotification/SendEmailAcknowledgement`, emailData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          console.error('Failed to submit celebration:', response.data);
        }
      } catch (error) {
        console.error('Error submitting celebration:', error);
      }
    }
  };

  // 6) RENDERING UI COMPONENTS FOR EACH STEP
  // Step 0: Show main categories
  const renderCardContent = () => (
    <div>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '350px',
        marginTop: '-12px',
      }}>
        {categorycards.map((card, index) => (
          <div
            key={index}
            style={{
              textAlign: 'center',
              borderRadius: '18px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: selectedCard === card.title ? '4px solid #1890ff' : 'none',
              boxShadow: selectedCard === card.title ? '0 0 10px rgba(0, 0, 0, 0.1)' : 'none',
              flexWrap: 'wrap',
              height: '190px',
            }}
            onClick={() => handleCardClick(card.title)}
          >
            <ActionCardInsidewig
              icon={card.icon}
              title={card.title === 'Happy Birthday' ? 'Birthday' : card.title}
              message={''}
              background={'linear-gradient(239.03deg, #E6FFCC 0%, #69C3A2 97.46%'}
              border={'1px solid #68C4D8'}
              variant="default"
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderRecipientSelection = () => {
    const combinedResults = [...recipientSearchResults, ...recipientSearchResultsSameTeam];
    const uniqueResults = Array.from(new Set(combinedResults.map(item => item.id)))
      .map(id => combinedResults.find(item => item.id === id));

    return (
      <div style={{ display: 'flex', justifyContent: "center", gap: '16px', alignItems: 'flex-start', WebkitJustifyContent: 'space-evenly', maxWidth: '100%', height: '78%' }}>
        <div style={{ width: '40%', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.65)',
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22px',
            }}
          >
            <span style={{ color: 'red' }}>*</span>
            {t('select_recipient')}
          </p>
          <Select
            showSearch
            placeholder="Search nominee"
            value={recipient}
            onSearch={setSearchTerm}
            onChange={handleRecipientSelect}
            filterOption={true}
            style={{
              flex: 1, marginBottom: '16px',
              width: '100%', // Full width for mobile
            }}
          >
            {uniqueResults.map((item) => item && (
              <Option key={item.id} value={item.displayName} label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(item.displayName) }}>{item.displayName.charAt(0)}</Avatar>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{item.displayName}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{item.email}</div>
                  </div>
                </div>
              }>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(item.displayName) }}>{item.displayName.charAt(0)}</Avatar>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{item.displayName}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>{item.email}</div>
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ width: '0.5px', height: '391px', background: 'var(--Colors-Neutral-Border-colorBorder, #D9D9D9)' }}></div>
        <div>
          <p
            style={{
              color: 'rgba(0, 0, 0, 0.65)',
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '22px',
            }}
          >
            {t('suggested_members')}
          </p>
          <List
            style={{ flex: 1, justifyContent: "center", maxWidth: '300px', height: '300px', overflowY: 'auto', backgroundColor: 'white' }}
            dataSource={recipientSearchResultsSameTeam}
            renderItem={(item) => item && (
              <List.Item
                onClick={() => handleRecipientSelect(item.displayName, { key: item.id })}
                style={{
                  maxWidth: '500px',
                  justifyContent: "flex-start",
                  cursor: 'pointer',
                  backgroundColor: recipient === item.displayName ? '#e6f7ff' : 'white',
                  border: recipient === item.displayName ? '1px solid #1890ff' : '1px solid #d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px',
                  marginBottom: '4px', // Add margin between items
                  borderRadius: '8px', // Add border radius for better appearance
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', // Add shadow for better appearance
                }}
              >
                <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(item.displayName) }}>{item.displayName.charAt(0)}</Avatar>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{item.displayName}</div>
                  <div style={{ fontSize: '12px', color: '#888' }}>{item.email}</div>
                </div>
              </List.Item>
            )}
          />
        </div>

      </div>
    );
  };

  // Step 3 or 2: E-card & message
  const isDefaultEcard = (ecard: ECard): boolean => {
    return !!ecard.background;
  };

  const handleImageSelect = (imageBytes: string, imageId: string) => {
    const selectedEcard = ecards.find(e => e.id === imageId);
    if (selectedEcard) {
      setSelectedImageId(imageId);
      setSelectedEcardData(selectedEcard);
      setHasUnsavedChanges(true);
    }
  };

  const handleViewImage = (imageBytes: string) => {
    setSelectedImage(imageBytes);
    setIsFullScreen(true);
  };



  const renderImageUpload = () => {
    // Maximum number of cards to display initially
    const MAX_VISIBLE_CARDS = 3;

    const handleECardSelect = (imageBytes: any, imageId: any) => {
      const selectedEcard = ecards.find(e => e.id === imageId);
      if (selectedEcard) {
        setSelectedImageId(imageId);
        setSelectedEcardData(selectedEcard);
        setHasUnsavedChanges(true);
        // Close the modal if it was open
        setShowCardsModal(false);
      }
    };

    const handleViewAllCards = () => {
      setShowCardsModal(true);
    };

    return (
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: "8px 24px 0",
          fontFamily: "Roboto, sans-serif",
        }}
      >
        <div>
          <p
            style={{
              color: "rgba(0, 0, 0, 0.85)",
              fontSize: "14px",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            <span style={{ color: "red" }}>*</span>
            {t('message')}
          </p>
          <TextArea
            placeholder={t('write_message_here')}
            value={description}
            maxLength={1000}
            onChange={(e) => {
              setDescription(e.target.value.replace(/[^a-zA-Z0-9 .,\-!\n]/g, ''));
              setHasUnsavedChanges(true);
            }}
            rows={4}
            style={{
              fontSize: "14px",
              resize: "none",
              marginBottom: "2px",
              color: "#000000",
              fontWeight: "normal",
            }}
          />
          <div
            style={{
              color: "#888",
              fontSize: "12px",
              marginTop: "4px",
              marginBottom: "12px",
            }}
          >
          </div>
        </div>
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <p
              style={{
                color: "rgba(0, 0, 0, 0.85)",
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              <span style={{ color: "red" }}>*</span>
              {uploadedImage ? t('selected_imagee') : t('select_ecard')}
            </p>
            <div>
              <div >

                <Tooltip title="Please upload jpeg, jpg or png only">
                  <div
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      backgroundColor: 'white',
                      color: 'black',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      cursor: 'pointer',
                      boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.1)',
                    }}
                    onClick={() => document.getElementById('file-upload')?.click()}
                  >
                    {t('upload_photo')}
                  </div>
                </Tooltip>
                <input
                  id="file-upload"
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </div>

          {uploadedImage ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: '16px',
                padding: '16px',
                border: '1px solid #d9d9d9',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#fafafa',
                position: 'relative', // For positioning the cross mark
              }}
            >
              <img
                src={URL.createObjectURL(uploadedImage)}
                alt="Uploaded"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  borderRadius: '8px',
                  marginBottom: '12px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              />
              {/* Cross mark for removing the image */}
              <div
                onClick={() => {
                  setUploadedImage(null);
                  setHasUnsavedChanges(true);
                }}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}
              >
                <span
                  style={{
                    fontSize: '16px',
                    color: '#ff4d4f',
                    fontWeight: 'bold',
                  }}
                >
                  ✕
                </span>
              </div>
            </div>
          ) : (
            <div>
              {/* Display selected card if one is chosen */}
              {selectedImageId && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ position: "relative", width: "210px", margin: "0 auto" }}>
                    {ecards
                      .filter(ecard => ecard.id === selectedImageId)
                      .map(ecard => (
                        <div
                          key={ecard.id}
                          style={{
                            height: "120px",
                            width: "210px",
                            borderRadius: "8px",
                            background: isDefaultEcard(ecard) ? ecard.background : undefined,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "12px",
                            border: "3px solid #1890ff",
                            overflow: "hidden",
                            boxShadow: "0 4px 8px rgba(24, 144, 255, 0.2)",
                            transition: "all 0.2s ease-in-out",
                            position: "relative",
                          }}
                          onClick={handleViewAllCards}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: "rgba(24, 144, 255, 0.05)",
                              pointerEvents: "none",
                            }}
                          />

                          <div
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              backgroundColor: "#1890ff",
                              color: "white",
                              padding: "2px 6px",
                              borderRadius: "10px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                              zIndex: 3,
                            }}
                          >
                            ✓ Selected
                          </div>

                          {isDefaultEcard(ecard) ? (
                            <div
                              style={{
                                background: "white",
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "14px",
                                fontWeight: "500",
                                zIndex: 1,
                              }}
                            >
                              {ecard.title || "eCard"}
                            </div>
                          ) : (
                            <div style={{ position: "relative", width: "100%", height: "100%" }}>
                              <img
                                src={ecard.imageBytes}
                                alt="eCard"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "4px",
                                }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "5px",
                                  right: "5px",
                                  display: "flex",
                                  gap: "5px",
                                  zIndex: 2,
                                }}
                              >
                                <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  }}
                                >
                                  <CheckCircleOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                                </div>
                                <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewImage(ecard.imageBytes);
                                  }}
                                >
                                  <EyeOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                  <Button
                    type="link"
                    onClick={handleViewAllCards}
                    style={{
                      display: "block",
                      margin: "-1px auto",
                      padding: "0",
                      fontSize: "12px",
                      fontWeight: "500"
                    }}
                  >
                    {t('change_Ecard')}
                  </Button>
                </div>
              )}

              {/* Only show card grid if no card is selected */}
              {!selectedImageId && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "16px",
                    marginBottom: "16px",
                  }}
                >
                  {ecards.slice(0, MAX_VISIBLE_CARDS - 1).map((ecard) => (
                    <div
                      key={ecard.id}
                      style={{
                        height: "120px",
                        borderRadius: "8px",
                        background: isDefaultEcard(ecard) ? ecard.background : undefined,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        overflow: "hidden",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s ease-in-out",
                        position: "relative",
                      }}
                      onClick={() => handleECardSelect(ecard.imageBytes, ecard.id)}
                    >
                      {isDefaultEcard(ecard) ? (
                        <div
                          style={{
                            background: "white",
                            padding: "6px 12px",
                            borderRadius: "20px",
                            fontSize: "14px",
                            fontWeight: "500",
                            zIndex: 1,
                          }}
                        >
                          {ecard.title || "eCard"}
                        </div>
                      ) : (
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <img
                            src={ecard.imageBytes}
                            alt="eCard"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              display: "flex",
                              gap: "5px",
                              zIndex: 2,
                            }}
                          >
                            <div
                              style={{
                                width: "26px",
                                height: "26px",
                                borderRadius: "50%",
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                              }}
                              className="eye-icon-container"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = "1";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = "0";
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewImage(ecard.imageBytes);
                              }}
                            >
                              <EyeOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* "Show more" card (only if there are more than MAX_VISIBLE_CARDS cards) */}
                  {ecards.length > 2 && (
                    <div
                      style={{
                        height: "120px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px",
                        border: "1px solid #d9d9d9",
                        overflow: "hidden",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        position: "relative",
                        background: ecards.length > 2 ? (
                          isDefaultEcard(ecards[2]) ? ecards[2].background : "white"
                        ) : "white",
                      }}
                      onClick={handleViewAllCards}
                    >
                      {/* Show the third card blurred in the background */}
                      {ecards.length > 2 && !isDefaultEcard(ecards[2]) && (
                        <img
                          src={ecards[2].imageBytes}
                          alt="eCard Preview"
                          style={{
                            position: "absolute",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            filter: "blur(5px)",
                            opacity: 0.7,
                          }}
                        />
                      )}

                      {/* Overlay with "Show more" text */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          color: "white",
                          zIndex: 2,
                        }}
                      >
                        <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                          + {ecards.length - 2} More
                        </span>
                        <span style={{ fontSize: "14px", marginTop: "4px" }}>
                          View All
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* All Cards Modal */}
        <Modal
          title="Select an eCard"
          visible={showCardsModal}
          onCancel={() => setShowCardsModal(false)}
          footer={null}
          width={800}
          centered
          bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px",
              padding: "16px",
            }}
          >
            {ecards.map((ecard) => {
              const isThisCardSelected = selectedImageId === ecard.id;

              return (
                <div
                  key={ecard.id}
                  style={{
                    height: "120px",
                    borderRadius: "8px",
                    background: isDefaultEcard(ecard) ? ecard.background : undefined,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px",
                    border: isThisCardSelected ? "3px solid #1890ff" : "1px solid #d9d9d9",
                    overflow: "hidden",
                    boxShadow: isThisCardSelected
                      ? "0 4px 8px rgba(24, 144, 255, 0.2)"
                      : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    position: "relative",
                  }}
                  onClick={() => handleECardSelect(ecard.imageBytes, ecard.id)}
                >
                  {isThisCardSelected && (
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        backgroundColor: "#1890ff",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                        zIndex: 3,
                      }}
                    >
                      ✓ Selected
                    </div>
                  )}

                  {isDefaultEcard(ecard) ? (
                    <div
                      style={{
                        background: "white",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "14px",
                        fontWeight: "500",
                        zIndex: 1,
                      }}
                    >
                      {ecard.title || "eCard"}
                    </div>
                  ) : (
                    <img
                      src={ecard.imageBytes}
                      alt="eCard"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Modal>

        {/* Full-screen image view modal */}
        {selectedImage && (
          <Modal
            visible={isFullScreen}
            footer={null}
            onCancel={() => setIsFullScreen(false)}
            width="100%"
            style={{ top: 0, height: "100vh" }}
            bodyStyle={{ height: "calc(100vh - 55px)", overflow: "auto" }}
          >
            <img
              src={selectedImage}
              alt="Full Screen"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Modal>
        )}
      </div>
    );
  };

  const renderFestivalSelection = () => {
    // Maximum number of cards to display initially
    const MAX_VISIBLE_CARDS = 5;

    // Generate a background color based on the category name for visual diversity
    const getColorFromName = (name: string) => {
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      // Make colors pastel and lighter
      let r = (hash & 0xFF) % 200 + 55;
      let g = ((hash >> 8) & 0xFF) % 200 + 55;
      let b = ((hash >> 16) & 0xFF) % 200 + 55;
      return `rgb(${r}, ${g}, ${b})`;
    };

    const getFestivalIcon = (name: string) => {
      const festivalIcons: { [key: string]: string } = {
        "Happy New Year": HappyNewYear,
        "Happy Halloween": HappyHalloween,
        "Friendship Day": FriendshipDay,
        "Happy Diwali": HappyDiwali,
        "Happy Thanksgiving": HappyThanksgiving,
        "Merry Christmas": MerryChristmas,
      };

      const iconSrc = Object.keys(festivalIcons).find(festival => name.includes(festival));
      return iconSrc ? <img src={festivalIcons[iconSrc]} alt={name} style={{ width: '222px', height: '140px' }} /> : <GiftOutlined style={{ fontSize: '64px' }} />;
    };

    return (
      <div>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          marginBottom: '16px',
          color: 'rgba(0, 0, 0, 0.85)',
          textAlign: 'center',
          fontFamily: 'Roboto, sans-serif',
        }}>
          <span style={{ color: 'red' }}>*</span> {t('select_festival')}
        </h3>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '350px',
          marginTop: '-12px'
        }}>
          {/* Show only the first MAX_VISIBLE_CARDS festivals */}
          {festivalCategories.slice(0, MAX_VISIBLE_CARDS).map((category) => (
            <div
              key={category.celebrationCategoryId}
              style={{
                textAlign: 'center',
                borderRadius: '17px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: selectedFestivalCategory === category.celebrationCategoryId ? '4px solid #1890ff' : 'none',
                boxShadow: selectedFestivalCategory === category.celebrationCategoryId ? '0 0 10px rgba(0, 0, 0, 0.1)' : 'none',
                flexWrap: 'wrap',
              }}
              onClick={() => handleFestivalCategorySelect(category.name, category.celebrationCategoryId)}
            >
              {getFestivalIcon(category.name)}
              {/* <div style={{ marginTop: '16px', fontSize: '18px', fontWeight: 'bold' }}>
                {category.name}
              </div> */}
            </div>
          ))}


          {/* Only show the "View More" card if there are more than MAX_VISIBLE_CARDS festivals */}
          {festivalCategories.length > MAX_VISIBLE_CARDS && (
            <div
              style={{
                textAlign: 'center',
                width: '214px',
                height: '144px',
                position: 'relative',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
              }}
              onClick={() => setShowFestivalModal(true)}
            >
              {/* Show the third festival card blurred in the background */}
              {festivalCategories.length > MAX_VISIBLE_CARDS && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: getColorFromName(festivalCategories[MAX_VISIBLE_CARDS].name),
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  filter: 'blur(5px)',
                }}>
                  {getFestivalIcon(festivalCategories[MAX_VISIBLE_CARDS].name)}
                  {/* <div style={{ marginTop: '16px', fontSize: '18px', fontWeight: 'bold' }}>
                    {festivalCategories[MAX_VISIBLE_CARDS].name}
                  </div> */}
                </div>
              )}

              {/* Overlay with "View more" text */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'white',
                  zIndex: 2,
                }}
              >
                <span style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                  + {festivalCategories.length - MAX_VISIBLE_CARDS} More
                </span>
                <span style={{ fontSize: '16px' }}>
                  View All Festivals
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal to display all festival options */}
        <Modal
          title={t('select_festival')}
          visible={showFestivalModal}
          onCancel={() => setShowFestivalModal(false)}
          footer={null}
          width={800}
          centered
          bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
        >
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px',
            padding: '16px'
          }}>
            {festivalCategories.map((category) => (
              <div
                key={category.celebrationCategoryId}
                style={{
                  textAlign: 'center',
                  borderRadius: '19px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: selectedFestivalCategory === category.celebrationCategoryId ? '4px solid #1890ff' : 'none',
                  boxShadow: selectedFestivalCategory === category.celebrationCategoryId ?
                    '0 0 10px rgba(24, 144, 255, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                  position: 'relative',

                }}
                onClick={() => {
                  handleFestivalCategorySelect(category.name, category.celebrationCategoryId);
                  setShowFestivalModal(false);
                }}
              >
                {getFestivalIcon(category.name)}
                {/* <div style={{ marginTop: '16px', fontSize: '16px', fontWeight: 'bold' }}>
                  {category.name}
                </div> */}

                {/* Selected indicator */}
                {selectedFestivalCategory === category.celebrationCategoryId && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: '#1890ff',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      zIndex: 3,
                    }}
                  >
                    ✓ Selected
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal>
      </div>
    );
  };


  // Step 4 or 3: Preview
  // Updated renderReview function to handle clicking on the eCard in preview section
  const renderReview = () => {
    const handleECardClick = () => {

      setShowCardsModal(true);
    };


    const handleECardSelect = (imageBytes: string, imageId: string) => {
      const selectedEcard = ecards.find(e => e.id === imageId);
      if (selectedEcard) {
        setSelectedImageId(imageId);
        setSelectedEcardData(selectedEcard);
        setHasUnsavedChanges(true);
      }
    };

    return (
      <div style={{
        maxHeight: "400px",
        overflowY: "auto",
        padding: "8px 24px 32px",
        borderRadius: "8px",
        fontFamily: 'Roboto, sans-serif',
        marginBottom: '18px'
      }}>
        {/* Category Selection */}
        <div style={{ marginBottom: "16px" }}>
          <p style={{
            color: 'rgba(0, 0, 0, 0.85)',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px'
          }}>
             {t('selected_category')}
          </p>
          <div
            style={{
              display: 'inline-flex',
              padding: '4px 12px',
              borderRadius: '16px',
              background: selectedCard === 'Happy Birthday' ? '#F3E8FF' :
                selectedCard === 'Work Anniversary' ? '#E0F2FE' :
                  selectedCard === 'Festivals' ? '#DCFCE7' : '#f0f0f0',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setCurrentStep(0)}
          >
            <span style={{ marginRight: '4px' }}>
              {selectedCard === 'Happy Birthday' ? <GiftOutlined /> :
                selectedCard === 'Work Anniversary' ? <GiftOutlined /> :
                  selectedCard === 'Festivals' ? <GiftOutlined /> : null}
            </span>
            <span style={{ fontWeight: '500' }}>{selectedCard}</span>
          </div>
        </div>

        {/* Festival Category (if applicable) */}
        {selectedCard === 'Festivals' && selectedFestivalCategory && (
          <div style={{ marginBottom: "16px" }}>
            <p style={{
              color: 'rgba(0, 0, 0, 0.85)',
              fontSize: '14px',
              fontWeight: '500',
              marginBottom: '8px'
            }}>
              {t('selected_festival')}
            </p>
            <div
              style={{
                display: 'inline-flex',
                padding: '4px 12px',
                borderRadius: '16px',
                background: '#DCFCE7',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => setCurrentStep(1)}
            >
              <span style={{ fontWeight: '500' }}>
                {festivalCategories.find(f => f.celebrationCategoryId === selectedFestivalCategory)?.name}
              </span>
            </div>
          </div>
        )}

        {/* Recipient Section */}
        <div style={{ marginBottom: "16px" }}>
          <p style={{
            color: 'rgba(0, 0, 0, 0.85)',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px'
          }}>
            {t('selected_recipient')}
          </p>
          {recipientDetails && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: '8px',
                width: 'fit-content',
                cursor: 'pointer',
                fontSize: '14px',
                background: '#E0F7FA'
              }}
              onClick={() => setCurrentStep(selectedCard === 'Festivals' ? 2 : 1)}
            >
              <Avatar style={{ marginRight: '8px', backgroundColor: getColorFromName(recipientDetails.displayName) }}>
                {recipientDetails.displayName.charAt(0)}
              </Avatar>
              <div>
                <div style={{ fontWeight: '500' }}>{recipientDetails.displayName}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{recipientDetails.email}</div>
              </div>
            </div>
          )}
        </div>

        {/* Message Section */}
        <div style={{ marginBottom: "24px" }}>
          <p style={{
            color: 'rgba(0, 0, 0, 0.85)',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px'
          }}>
            {t('your_message')}
          </p>
          <TextArea
            value={description}
            rows={3}
            readOnly
            style={{
              resize: 'none',
              fontSize: '14px',
              fontFamily: 'Roboto, sans-serif',
              cursor: 'pointer'
            }}
            onClick={() => setCurrentStep(selectedCard === 'Festivals' ? 3 : 2)}
          />
        </div>

        {/* eCard Section */}
        {/* eCard Section */}
        <div style={{ paddingBottom: '24px' }}>
          <p style={{
            color: 'rgba(0, 0, 0, 0.85)',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '8px'
          }}>
            {t('selected_imagee')}
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '16px',
            position: 'relative'
          }}>
            {uploadedImage ? (
              <div
                style={{
                  height: '120px',
                  width: '210px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  overflow: 'hidden',
                  marginBottom: '16px',
                  border: '1px solid #d9d9d9',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                }}
              >
                <img
                  src={URL.createObjectURL(uploadedImage)}
                  alt="Uploaded"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </div>
            ) : selectedEcardData && (
              <div
                style={{
                  height: '120px',
                  width: '210px',
                  borderRadius: '8px',
                  background: isDefaultEcard(selectedEcardData) ? selectedEcardData.background : undefined,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '12px',
                  overflow: 'hidden',
                  marginBottom: '16px',
                  border: '1px solid #d9d9d9',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  position: 'relative',
                }}
                onClick={handleECardClick}
              >
                {isDefaultEcard(selectedEcardData) ? (
                  <div style={{
                    background: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '14px',
                    fontWeight: '500',
                    zIndex: 2,
                    position: 'relative'
                  }}>
                    {selectedEcardData.title || "eCard"}
                  </div>
                ) : (
                  <img
                    src={selectedEcardData.imageBytes}
                    alt="eCard"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '4px'
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {/* eCard Section end*/}

        <div>
          <Checkbox
            checked={isVisibleToSocialWall}
            onChange={handleCheckboxChange}
            style={{ marginBottom: '15px' }}
          >
            {t('share_feeds')}
          </Checkbox>
        </div>

        {/* All Cards Modal - Reused from renderImageUpload */}
        <Modal
          title="Select an eCard"
          visible={showCardsModal}
          onCancel={() => setShowCardsModal(false)}
          footer={null}
          width={800}
          centered
          bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px",
              padding: "16px",
            }}
          >
            {ecards.map((ecard) => {
              const isThisCardSelected = selectedImageId === ecard.id;

              return (
                <div
                  key={ecard.id}
                  style={{
                    height: "120px",
                    borderRadius: "8px",
                    background: isDefaultEcard(ecard) ? ecard.background : undefined,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px",
                    border: isThisCardSelected ? "3px solid #1890ff" : "1px solid #d9d9d9",
                    overflow: "hidden",
                    boxShadow: isThisCardSelected
                      ? "0 4px 8px rgba(24, 144, 255, 0.2)"
                      : "0 2px 4px rgba(0, 0, 0, 0.1)",
                    position: "relative",
                  }}
                  onClick={() => {
                    handleECardSelect(ecard.imageBytes, ecard.id);
                    setShowCardsModal(false);
                  }}
                >
                  {isThisCardSelected && (
                    <div
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        backgroundColor: "#1890ff",
                        color: "white",
                        padding: "2px 6px",
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                        zIndex: 3,
                      }}
                    >
                      ✓ Selected
                    </div>
                  )}

                  {isDefaultEcard(ecard) ? (
                    <div
                      style={{
                        background: "white",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "14px",
                        fontWeight: "500",
                        zIndex: 1,
                      }}
                    >
                      {ecard.title || "eCard"}
                    </div>
                  ) : (
                    <img
                      src={ecard.imageBytes}
                      alt="eCard"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "4px",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Modal>
      </div>
    );
  };

  // 7) DYNAMIC STEP HEADERS
  // Custom dots for Steps component
  const customDot: StepsProps['progressDot'] = (dot, { status, index }) => (
    <Popover
      content={
        <span>
          {selectedCard === 'Festivals' ?
            (index === 0 ? 'Select your celebration category' :
              index === 1 ? 'Choose a festival' :
                index === 2 ? 'Choose who you want to celebrate' :
                  index === 3 ? 'Write your message and pick an eCard' :
                    'Review your celebration details') :
            (index === 0 ? 'Select your celebration category' :
              index === 1 ? 'Choose who you want to celebrate' :
                index === 2 ? 'Write your message and pick an eCard' :
                  'Review your celebration details')
          }
        </span>
      }
    >
      {dot}
    </Popover>
  );

  const renderSteps = () => {
    if (selectedCard === 'Festivals') {
      // 5-step flow
      return (
        <Steps
          current={currentStep}
          style={{
            marginBottom: '6px',
            paddingTop: '24px',
            overflowX: 'auto', // Allow horizontal scrolling for steps on mobile
          }}
          progressDot={customDot}
          items={[
            { title: <span style={{ fontSize: '14px', fontFamily: 'Roboto' }}>{t('select_occasion')}</span> },
            { title: <span style={{ fontSize: '14px', fontFamily: 'Roboto' }}>{t('select_festival')}</span> },
            { title: <span style={{ fontSize: '14px', fontFamily: 'Roboto' }}>{t('select_recipient')}</span> },
            { title: <span style={{ fontSize: '14px', fontFamily: 'Roboto' }}>{t('message')}</span> },
            { title: <span style={{ fontSize: '14px', fontFamily: 'Roboto' }}>{t('preview')}</span> }
          ]}
        />
      );
    } else {
      // 4-step flow
      return (
        <div>
          <style>
            {`
              @media (max-width: 768px) {
                .hide-on-mobile {
                  display: none;
                }
              }
            `}
          </style>
          <div className="hide-on-mobile">
            <Steps
              current={currentStep}
              style={{
                marginBottom: '6px',
                paddingTop: '24px',
                overflowX: 'auto', // Allow horizontal scrolling for steps on mobile
              }}
              progressDot={customDot}
              items={[
                { title: <span style={{ fontSize: '14px' }}>{t('select_occasion')}</span> },
                { title: <span style={{ fontSize: '14px' }}>{t('select_recipient')}</span> },
                { title: <span style={{ fontSize: '14px' }}>{t('message_ecard')}</span> },
                { title: <span style={{ fontSize: '14px' }}>{t('preview')}</span> }
              ]}
            />
          </div>
        </div>);
    }
  };

  // 8) RENDER STEP CONTENT BASED ON "Festivals" OR NOT
  const renderContent = () => {
    // FESTIVAL-SPECIFIC FLOW (5 steps)
    if (selectedCard === 'Festivals') {
      switch (currentStep) {
        case 0:
          return renderCardContent(); // Select Category
        case 1:
          return renderFestivalSelection(); // Select Festival
        case 2:
          return renderRecipientSelection(); // Select Recipient
        case 3:
          return renderImageUpload(); // Message & eCard
        case 4:
          return renderReview(); // Preview
        default:
          return renderCardContent();
      }
    }

    // NON-FESTIVAL FLOW (4 steps)
    switch (currentStep) {
      case 0:
        return renderCardContent(); // Select Category
      case 1:
        return renderRecipientSelection(); // Select Recipient
      case 2:
        return renderImageUpload(); // Message & eCard
      case 3:
        return renderReview(); // Preview
      default:
        return renderCardContent();
    }
  };

  // 9) MAIN RETURN
  return (
    <Modal
      title={<span style={{
        fontFamily: 'Roboto, sans-serif',
        fontSize: '18px',
        fontWeight: 'bold'
      }}>{t('celebrate')}</span>}
      visible={visible}
      onCancel={handleModalClose}
      footer={null}
      centered
      width={800}
      bodyStyle={{
        maxHeight: 'calc(85vh - 55px)',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
      }}
    >
      <div style={{ padding: '16px 16px 0', flexShrink: 0 }}>
        {renderSteps()}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
        {renderContent()}
      </div>
      <div style={{
        flexShrink: 0,
        borderTop: '1px solid #f0f0f0',
        background: 'white',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
      }}>
        <div>
          {currentStep > 0 && (
            <Button
              onClick={handlePreviousStep}
              size='middle'
              style={{ fontSize: '16px', minWidth: '100px', height: '40px' }}
            >
              {t('back')}
            </Button>
          )}
        </div>
        <div>
          {selectedCard === 'Festivals' ?
            // Festival flow (5 steps)
            (currentStep > 0 && currentStep < 4 && (
              <Button
                type="primary"
                onClick={handleNextStep}
                size="middle"
                style={{ fontSize: '16px', minWidth: '100px', height: '40px' }}
                disabled={
                  (currentStep === 1 && !selectedFestivalCategory) ||
                  (currentStep === 2 && !recipient) ||
                  (currentStep === 3 && (!description || (!selectedImageId && !uploadedImage)))
                }
              >
                {currentStep === 3 && description ? t('preview') : t('next')}
              </Button>
            )) :
            // Non-festival flow (4 steps)
            (currentStep > 0 && currentStep < 3 && (
              <Button
                type="primary"
                onClick={handleNextStep}
                size="middle"
                style={{ fontSize: '16px', minWidth: '100px', height: '40px', backgroundColor: '#8629A4' }}
                disabled={
                  (currentStep === 1 && !recipient) ||
                  (currentStep === 2 && (!description || (!selectedImageId && !uploadedImage)))
                }
              >
                {currentStep === 2 && description ? t('preview') : t('next')}
              </Button>
            ))
          }
          {((selectedCard === 'Festivals' && currentStep === 4) ||
            (selectedCard !== 'Festivals' && currentStep === 3)) && (
              <Button
                type="primary"
                onClick={handleSubmit}
                size="large"
                style={{ fontSize: '16px', minWidth: '100px', height: '40px', backgroundColor: '#8629A4' }}
                disabled={!selectedCard || !recipient || !description || (!selectedImageId && !uploadedImage)}
              >
                {t('submit')}
              </Button>
            )}
        </div>
      </div>
    </Modal>
  );
};

export default Celebrate;